import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Field } from "./BankAccountsPage";
import { SegBadge } from "./OpeningBalancesPage";
import { Upload, Trash2, FilePlus2, CheckCircle2, AlertTriangle, Search, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function num(n, d = 2) {
    return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}
function fmtVol(n, segment) {
    if (n === null || n === undefined) return "—";
    if (segment === "Voice") return `${num(n, 2)} min`;
    return num(n, 0);
}

const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12 };
const pad = (n) => String(n).padStart(2, "0");
const valid = (y, m, d) => y >= 2000 && y <= 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31;
function inferDateFromFilename(name) {
    const base = (name || "").replace(/\.[^.]+$/, "");
    let m = base.match(/(20\d{2})[-_/]?(\d{2})[-_/]?(\d{2})/);
    if (m) {
        const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
        if (valid(y, mo, d)) return `${y}-${pad(mo)}-${pad(d)}`;
    }
    m = base.match(/(\d{1,2})[-_/](\d{1,2})[-_/](20\d{2})/);
    if (m) {
        const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])];
        if (valid(y, mo, d)) return `${y}-${pad(mo)}-${pad(d)}`;
    }
    m = base.match(/(\d{1,2})[-_\s]?([A-Za-z]{3,4})[-_\s]?(20\d{2})/);
    if (m) {
        const mo = MONTHS[m[2].toLowerCase()];
        const [d, y] = [Number(m[1]), Number(m[3])];
        if (mo && valid(y, mo, d)) return `${y}-${pad(mo)}-${pad(d)}`;
    }
    return "";
}

export default function SalesReconciliationTab({ canWrite, segment, period, entities, currencies, onInvoiced }) {
    const [tab, setTab] = useState("pending");
    const [pending, setPending] = useState([]);
    const [invoiced, setInvoiced] = useState([]);
    const [uploads, setUploads] = useState([]);
    const [loading, setLoading] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            // All three sub-tabs ignore the page-level Period filter — Pending +
            // Daily Uploads are operational/audit views, and Invoiced must show
            // the just-generated invoice immediately regardless of which month
            // the cycle bounds happen to fall in.
            const segParams = segment !== "all" ? { segment } : {};
            const [pn, iv, up] = await Promise.all([
                api.get("/sales/cycles", { params: { ...segParams, status: "pending" } }),
                api.get("/sales/cycles", { params: { ...segParams, status: "invoiced" } }),
                api.get("/sales/daily-uploads", { params: segParams }),
            ]);
            setPending(pn.data || []);
            setInvoiced(iv.data || []);
            setUploads(up.data || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [segment, period]);

    // Pending tab only shows cycles that have already ended (cycle_end < today),
    // so the tab badge mirrors that. Ongoing cycles are still in `pending` state
    // server-side but are filtered from view until their window closes.
    const pendingCompletedCount = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        return (pending || []).filter((c) => (c.cycle_end || "") < today).length;
    }, [pending]);

    return (
        <div data-testid="sales-recon-tab">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[12px] text-slate-500">
                    Daily server-data → cycle aggregation → one-click invoice generation. Volume = <span className="font-mono">Calculated</span>, amount = <span className="font-mono">Cost IN</span>. <span className="text-slate-400">Page-level period filter does not apply — all sub-tabs show the full operational queue.</span>
                </div>
                <div className="flex gap-2">
                    {canWrite && <RefreshCyclesButton onDone={reload}/>}
                    {canWrite && <SalesDailyUploadButton onDone={reload}/>}
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="rounded-sm bg-slate-100">
                    <TabsTrigger value="pending" className="rounded-sm" data-testid="sales-recon-tab-pending">Pending ({pendingCompletedCount})</TabsTrigger>
                    <TabsTrigger value="invoiced" className="rounded-sm" data-testid="sales-recon-tab-invoiced">Invoiced ({invoiced.length})</TabsTrigger>
                    <TabsTrigger value="uploads" className="rounded-sm" data-testid="sales-recon-tab-uploads">Daily Uploads ({uploads.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="pending" className="mt-3">
                    {loading && <div className="text-[12px] text-slate-500 py-2">Loading…</div>}
                    <SalesCyclesTable
                        cycles={pending}
                        kind="pending"
                        canWrite={canWrite}
                        entities={entities}
                        currencies={currencies}
                        onChanged={async () => { await reload(); onInvoiced?.(); }}
                    />
                </TabsContent>
                <TabsContent value="invoiced" className="mt-3">
                    <SalesCyclesTable cycles={invoiced} kind="invoiced" canWrite={canWrite} entities={entities} currencies={currencies} onChanged={reload}/>
                </TabsContent>
                <TabsContent value="uploads" className="mt-3">
                    <SalesUploadsTable uploads={uploads} canWrite={canWrite} onChanged={reload}/>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function RefreshCyclesButton({ onDone }) {
    const [busy, setBusy] = useState(false);
    const click = async () => {
        const msg = "Refresh all pending cycles?\n\n• Re-applies the current server-name mapping to every uploaded row\n• Rebuilds every pending cycle using each partner master's current cycle config (weekly / fortnightly / monthly + month-end-cut)\n• Invoiced and skipped cycles are preserved untouched\n\nThis is the right action after creating/updating partner masters or server-name mappings.";
        if (!window.confirm(msg)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/sales/refresh-cycles");
            toast.success(`Refreshed — ${data.remapped_uploads} uploads re-mapped, ${data.pending_cycles_rebuilt} pending cycles rebuilt (${data.invoiced_preserved} invoiced + ${data.skipped_preserved} skipped preserved)`);
            onDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Refresh failed");
        } finally { setBusy(false); }
    };
    return (
        <Button variant="outline" className="rounded-sm" onClick={click} disabled={busy} data-testid="sales-recon-refresh-btn" title="Re-apply server-name mapping & rebuild cycles using current partner master config">
            <RefreshCw size={14} className={`mr-1.5 ${busy ? "animate-spin" : ""}`}/>{busy ? "Refreshing…" : "Refresh"}
        </Button>
    );
}

function SalesDailyUploadButton({ onDone }) {
    const [open, setOpen] = useState(false);
    const [segment, setSegment] = useState("Voice");
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);

    const reset = () => { setItems([]); setBusy(false); };

    const onPick = (e) => {
        const picked = Array.from(e.target.files || []);
        const today = new Date().toISOString().slice(0, 10);
        const next = picked.map((f, i) => {
            const detected = inferDateFromFilename(f.name);
            return {
                id: `${Date.now()}-${i}-${f.name}`,
                file: f,
                date: detected || today,
                detected: !!detected,
                status: "pending",
                error: "",
            };
        });
        setItems((prev) => [...prev, ...next]);
        e.target.value = "";
    };

    const updateDate = (id, date) => setItems((prev) => prev.map((it) => it.id === id ? { ...it, date } : it));
    const removeItem = (id) => setItems((prev) => prev.filter((it) => it.id !== id));

    const submitAll = async () => {
        if (!items.length) return toast.error("Select at least one file");
        const missing = items.filter((it) => !it.date);
        if (missing.length) return toast.error(`${missing.length} file(s) missing a date`);
        const seen = new Set();
        for (const it of items) {
            const k = `${segment}|${it.date}`;
            if (seen.has(k)) return toast.error(`Duplicate date ${it.date} in batch — keep only one file per date`);
            seen.add(k);
        }
        setBusy(true);
        let ok = 0, fail = 0;
        for (const it of items) {
            setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, status: "uploading", error: "" } : x));
            try {
                const fd = new FormData();
                fd.append("segment", segment);
                fd.append("date", it.date);
                fd.append("file", it.file);
                await api.post("/sales/daily-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, status: "done" } : x));
                ok += 1;
            } catch (e) {
                const msg = e.response?.data?.detail || "Upload failed";
                setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, status: "error", error: msg } : x));
                fail += 1;
            }
        }
        setBusy(false);
        if (ok) toast.success(`Uploaded ${ok} file(s) & cycles updated${fail ? ` — ${fail} failed` : ""}`);
        else toast.error(`All ${fail} upload(s) failed`);
        onDone?.();
        if (!fail) { setOpen(false); reset(); }
    };

    const statusBadge = (s) => {
        if (s === "done") return <span className="text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">Done</span>;
        if (s === "uploading") return <span className="text-[10px] uppercase tracking-widest text-[#002FA7] bg-blue-50 border border-blue-200 px-1.5 py-0.5">Uploading…</span>;
        if (s === "error") return <span className="text-[10px] uppercase tracking-widest text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5">Error</span>;
        return <span className="text-[10px] uppercase tracking-widest text-slate-600 bg-slate-50 border border-slate-200 px-1.5 py-0.5">Pending</span>;
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm" data-testid="sales-recon-upload-btn">
                    <Upload size={14} className="mr-1.5"/>Upload Sales Data
                </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-3xl" data-testid="sales-recon-upload-dialog">
                <DialogHeader><DialogTitle>Upload daily sales server data</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Segment (applies to all files)">
                        <Select value={segment} onValueChange={setSegment}>
                            <SelectTrigger className="rounded-sm" data-testid="sales-recon-upload-segment"><SelectValue/></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Voice">Voice (USD base)</SelectItem>
                                <SelectItem value="SMS">SMS (EUR base)</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Pick files (multi-select)">
                        <Input type="file" accept=".csv,.xlsx" multiple onChange={onPick} className="rounded-sm h-9" data-testid="sales-recon-upload-file"/>
                    </Field>
                </div>

                {items.length > 0 && (
                    <div className="mt-3 border border-slate-200 max-h-[320px] overflow-y-auto" data-testid="sales-recon-upload-queue">
                        <table className="w-full text-[12.5px] financial">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                                    <th className="px-2 py-2">File</th>
                                    <th className="px-2 py-2 w-40">Data date</th>
                                    <th className="px-2 py-2 w-24">Status</th>
                                    <th className="px-2 py-2 w-10"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it) => (
                                    <tr key={it.id} className="border-b border-slate-100">
                                        <td className="px-2 py-1.5 truncate max-w-[240px]" title={it.file.name}>{it.file.name}</td>
                                        <td className="px-2 py-1.5">
                                            <Input type="date" value={it.date} onChange={(e) => updateDate(it.id, e.target.value)} disabled={busy || it.status === "done"} className="rounded-sm h-8 font-mono text-[12px]"/>
                                            {!it.detected && it.status !== "done" && (
                                                <div className="text-[10px] text-amber-700 mt-0.5" title="Filename had no parseable date — defaulted to today">no date in filename — verify</div>
                                            )}
                                        </td>
                                        <td className="px-2 py-1.5">
                                            {statusBadge(it.status)}
                                            {it.error && <div className="text-[10.5px] text-red-700 mt-0.5 truncate max-w-[180px]" title={it.error}>{it.error}</div>}
                                        </td>
                                        <td className="px-2 py-1.5">
                                            {!busy && it.status !== "done" && (
                                                <Button variant="ghost" size="sm" onClick={() => removeItem(it.id)} className="h-7 px-2 text-red-600"><Trash2 size={12}/></Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="text-[11.5px] text-slate-500 mt-2 leading-relaxed">
                    Same multi-section format as the Purchase upload. Volume = <span className="font-mono">Calculated</span> (MM:SS), amount = <span className="font-mono">Cost IN</span>. Server names are auto-mapped to canonical partners using the Server Name Mappings master.
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => { setOpen(false); reset(); }} disabled={busy}>Close</Button>
                    <Button onClick={submitAll} disabled={busy || items.length === 0} className="rounded-sm bg-[#002FA7]" data-testid="sales-recon-upload-submit">
                        {busy ? "Uploading…" : `Upload ${items.length || ""} file${items.length === 1 ? "" : "s"}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SalesUploadsTable({ uploads, canWrite, onChanged }) {
    const remove = async (id) => {
        if (!window.confirm("Delete this daily sales upload? Cycles will be recomputed.")) return;
        try {
            await api.delete(`/sales/daily-uploads/${id}`);
            onChanged?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    };
    return (
        <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="sales-uploads-table">
            <table className="w-full text-[13px] financial min-w-[800px]">
                <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                        <th className="px-3 py-2.5">Date</th>
                        <th className="px-3 py-2.5">Segment</th>
                        <th className="px-3 py-2.5">File</th>
                        <th className="px-3 py-2.5 num">Partners</th>
                        <th className="px-3 py-2.5 num">Total Volume</th>
                        <th className="px-3 py-2.5 num">Total Sales</th>
                        <th className="px-3 py-2.5 w-12"></th>
                    </tr>
                </thead>
                <tbody>
                    {uploads.length === 0 && <tr><td colSpan={7} className="text-center text-slate-500 py-10">No daily sales uploads in this period</td></tr>}
                    {uploads.map((u) => (
                        <tr key={u.id} className="border-b border-slate-100" data-testid={`sales-upload-row-${u.id}`}>
                            <td className="px-3 py-2 font-mono">{u.date}</td>
                            <td className="px-3 py-2"><SegBadge segment={u.segment}/></td>
                            <td className="px-3 py-2 text-slate-600 truncate max-w-[260px]">{u.filename}</td>
                            <td className="px-3 py-2 num">{(u.rows || []).length}</td>
                            <td className="px-3 py-2 num">{fmtVol(u.total_volume, u.segment)}</td>
                            <td className="px-3 py-2 num font-semibold">{num(u.total_amount)}</td>
                            <td className="px-3 py-2">
                                {canWrite && <Button variant="ghost" size="sm" onClick={() => remove(u.id)} className="h-7 px-2 text-red-600" data-testid={`sales-upload-delete-${u.id}`}><Trash2 size={13}/></Button>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function SortHeader({ label, field, sort, setSort, className = "" }) {
    const active = sort.field === field;
    const dir = active ? sort.dir : null;
    const Icon = !active ? ArrowUpDown : (dir === "asc" ? ArrowUp : ArrowDown);
    const onClick = () => {
        if (!active) setSort({ field, dir: "asc" });
        else if (dir === "asc") setSort({ field, dir: "desc" });
        else setSort({ field: null, dir: null });
    };
    return (
        <th className={`px-2 py-2 ${className}`}>
            <button type="button" onClick={onClick} className={`inline-flex items-center gap-1 hover:text-[#002FA7] ${active ? "text-[#002FA7]" : ""}`} data-testid={`sales-recon-sort-${field}`}>
                {label}
                <Icon size={11} className={active ? "" : "opacity-40"}/>
            </button>
        </th>
    );
}

function SalesCyclesTable({ cycles, kind, canWrite, entities, currencies, onChanged }) {
    const [query, setQuery] = useState("");
    const [sort, setSort] = useState({ field: "partner_name", dir: "asc" });
    const [cycleTypeFilter, setCycleTypeFilter] = useState("all"); // all | weekly | fortnightly | monthly
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkOpen, setBulkOpen] = useState(false);

    const allowSelect = (kind === "pending") && canWrite;

    // Today (local YYYY-MM-DD) — used to hide cycles that haven't ended yet.
    // Only applies to the Pending tab; Invoiced cycles always show.
    const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

    const filtered = useMemo(() => {
        let rows = cycles;
        if (kind === "pending") {
            // Hide cycles where the end date is today or in the future — the
            // cycle window is still ongoing, so no invoice should be generated yet.
            rows = rows.filter((c) => (c.cycle_end || "") < today);
        }
        if (cycleTypeFilter !== "all") {
            rows = rows.filter((c) => (c.cycle_type || "").toLowerCase() === cycleTypeFilter);
        }
        const q = query.trim().toLowerCase();
        if (q) {
            rows = rows.filter((c) => (c.partner_name || "").toLowerCase().includes(q));
        }
        if (sort.field) {
            const dir = sort.dir === "desc" ? -1 : 1;
            const get = (c) => {
                const v = c[sort.field];
                if (typeof v === "number") return v;
                return String(v ?? "").toLowerCase();
            };
            rows = [...rows].sort((a, b) => {
                const va = get(a), vb = get(b);
                if (va < vb) return -1 * dir;
                if (va > vb) return 1 * dir;
                return 0;
            });
        }
        return rows;
    }, [cycles, query, sort, cycleTypeFilter, kind, today]);

    // Counts per cycle type — calculated from the same base set the table operates on
    // (i.e., already excludes ongoing-window pending cycles) so chip counts match the table.
    const baseRows = useMemo(() => {
        if (kind !== "pending") return cycles;
        return cycles.filter((c) => (c.cycle_end || "") < today);
    }, [cycles, kind, today]);
    const typeCounts = useMemo(() => {
        const m = { all: baseRows.length, weekly: 0, fortnightly: 0, monthly: 0 };
        for (const c of baseRows) {
            const t = (c.cycle_type || "").toLowerCase();
            if (t in m) m[t] += 1;
        }
        return m;
    }, [baseRows]);
    const ongoingHidden = (kind === "pending") ? (cycles.length - baseRows.length) : 0;

    const chipCls = (active) => `px-2.5 h-7 rounded-sm text-[11px] uppercase tracking-widest border transition-colors ${active ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`;

    // Drop selections that fall outside the current filtered set (e.g. user
    // changed cycle-type filter or searched). Keeps "Generate selected" honest.
    useEffect(() => {
        if (!allowSelect) return;
        const visibleIds = new Set(filtered.map((c) => c.id));
        setSelectedIds((prev) => {
            const next = new Set();
            for (const id of prev) if (visibleIds.has(id)) next.add(id);
            return next.size === prev.size ? prev : next;
        });
    }, [filtered, allowSelect]);

    const toggleOne = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAllVisible = () => {
        setSelectedIds((prev) => {
            const allSelected = filtered.length > 0 && filtered.every((c) => prev.has(c.id));
            if (allSelected) return new Set();
            const next = new Set(prev);
            for (const c of filtered) next.add(c.id);
            return next;
        });
    };
    const allVisibleSelected = filtered.length > 0 && filtered.every((c) => selectedIds.has(c.id));
    const someVisibleSelected = filtered.some((c) => selectedIds.has(c.id));
    const selectedCycles = useMemo(() => filtered.filter((c) => selectedIds.has(c.id)), [filtered, selectedIds]);

    return (
        <div data-testid={`sales-recon-${kind}-section`}>
            <div className="flex items-center justify-between mb-2 gap-2">
                <div className="relative w-full max-w-sm">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search partner…"
                        className="rounded-sm h-8 pl-8 text-[12.5px]"
                        data-testid={`sales-recon-${kind}-search`}
                    />
                </div>
                <div className="text-[11px] text-slate-500 whitespace-nowrap">
                    {filtered.length === baseRows.length ? `${baseRows.length}` : `${filtered.length} of ${baseRows.length}`} cycle{baseRows.length === 1 ? "" : "s"}
                </div>
            </div>
            {kind === "pending" && (
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-widest text-slate-400 mr-1">Cycle:</span>
                    <button type="button" onClick={() => setCycleTypeFilter("all")} className={chipCls(cycleTypeFilter === "all")} data-testid="sales-recon-filter-all">All ({typeCounts.all})</button>
                    <button type="button" onClick={() => setCycleTypeFilter("weekly")} className={chipCls(cycleTypeFilter === "weekly")} data-testid="sales-recon-filter-weekly">Weekly ({typeCounts.weekly})</button>
                    <button type="button" onClick={() => setCycleTypeFilter("fortnightly")} className={chipCls(cycleTypeFilter === "fortnightly")} data-testid="sales-recon-filter-fortnightly">Fortnightly ({typeCounts.fortnightly})</button>
                    <button type="button" onClick={() => setCycleTypeFilter("monthly")} className={chipCls(cycleTypeFilter === "monthly")} data-testid="sales-recon-filter-monthly">Monthly ({typeCounts.monthly})</button>
                    {ongoingHidden > 0 && (
                        <span className="ml-2 text-[10.5px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-sm" title="Cycles whose end date is today or later are hidden — they'll appear once the cycle window has fully closed.">
                            {ongoingHidden} ongoing cycle{ongoingHidden === 1 ? "" : "s"} hidden (window not yet closed)
                        </span>
                    )}
                </div>
            )}
            {allowSelect && selectedIds.size > 0 && (
                <div className="flex items-center justify-between gap-3 mb-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-sm" data-testid="sales-recon-bulk-bar">
                    <div className="text-[12.5px] text-[#002FA7]">
                        <strong>{selectedIds.size}</strong> cycle{selectedIds.size === 1 ? "" : "s"} selected
                        <span className="ml-2 text-[11px] text-slate-500 font-mono">
                            Σ Sales: {num(selectedCycles.reduce((s, c) => s + Number(c.our_amount || 0), 0))}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" className="h-8 rounded-sm text-slate-600" onClick={() => setSelectedIds(new Set())} data-testid="sales-recon-bulk-clear">Clear</Button>
                        <Button size="sm" className="h-8 rounded-sm bg-[#002FA7] hover:bg-[#001f6f]" onClick={() => setBulkOpen(true)} data-testid="sales-recon-bulk-generate-btn">
                            <FilePlus2 size={13} className="mr-1.5"/>Generate {selectedIds.size} selected
                        </Button>
                    </div>
                </div>
            )}
            <div className="bg-white border border-slate-200 overflow-x-auto" data-testid={`sales-recon-${kind}-table`}>
                <table className="w-full text-[12.5px] financial min-w-[1100px]">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            {allowSelect && (
                                <th className="px-2 py-2 w-9">
                                    <Checkbox
                                        checked={allVisibleSelected}
                                        onCheckedChange={toggleAllVisible}
                                        data-state={allVisibleSelected ? "checked" : (someVisibleSelected ? "indeterminate" : "unchecked")}
                                        data-testid="sales-recon-bulk-header-checkbox"
                                        aria-label="Select all visible cycles"
                                    />
                                </th>
                            )}
                            <SortHeader label="Cycle" field="cycle_start" sort={sort} setSort={setSort}/>
                            <th className="px-2 py-2">Seg</th>
                            <SortHeader label="Partner" field="partner_name" sort={sort} setSort={setSort}/>
                            <SortHeader label="Volume" field="our_volume" sort={sort} setSort={setSort} className="num"/>
                            <SortHeader label="Sales (Cost IN)" field="our_amount" sort={sort} setSort={setSort} className="num"/>
                            {kind === "invoiced" && <SortHeader label="Invoice #" field="invoice_number" sort={sort} setSort={setSort}/>}
                            {kind === "invoiced" && <th className="px-2 py-2">Entity</th>}
                            {kind === "pending" && <th className="px-2 py-2">Data</th>}
                            <th className="px-2 py-2 w-44"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && <tr><td colSpan={(kind === "invoiced" ? 8 : 7) + (allowSelect ? 1 : 0)} className="text-center text-slate-500 py-10">{query.trim() ? `No partners match "${query}".` : (kind === "pending" ? "No pending cycles. Upload daily sales data to generate them." : "No invoiced cycles in this period.")}</td></tr>}
                        {filtered.map((c) => (
                            <SalesCycleRow
                                key={c.id}
                                c={c}
                                kind={kind}
                                canWrite={canWrite}
                                entities={entities}
                                currencies={currencies}
                                onChanged={onChanged}
                                selectable={allowSelect}
                                selected={selectedIds.has(c.id)}
                                onToggleSelect={() => toggleOne(c.id)}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
            {bulkOpen && (
                <BulkGenerateInvoiceDialog
                    cycles={selectedCycles}
                    entities={entities}
                    currencies={currencies}
                    onClose={() => setBulkOpen(false)}
                    onChanged={async () => {
                        setSelectedIds(new Set());
                        await onChanged?.();
                    }}
                />
            )}
        </div>
    );
}

function SalesCycleRow({ c, kind, canWrite, entities, currencies, onChanged, selectable, selected, onToggleSelect }) {
    const [genOpen, setGenOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const skip = async () => {
        if (!window.confirm(`Skip ${c.partner_name} for ${c.cycle_start} → ${c.cycle_end}?\n\nIt will be hidden until you unskip it.`)) return;
        setBusy(true);
        try {
            await api.post(`/sales/cycles/${c.id}/skip`);
            toast.success(`Skipped ${c.partner_name}`);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Skip failed");
        } finally { setBusy(false); }
    };

    const remove = async () => {
        if (!window.confirm(`DELETE the cycle for ${c.partner_name} (${c.cycle_start} → ${c.cycle_end})?\n\nThis is permanent. Re-uploading the daily data for this period will recreate it.`)) return;
        setBusy(true);
        try {
            await api.delete(`/sales/cycles/${c.id}`);
            toast.success(`Deleted cycle for ${c.partner_name}`);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        } finally { setBusy(false); }
    };

    const revertInvoice = async () => {
        const msg = `DELETE invoice ${c.invoice_number || ""} for ${c.partner_name}?\n\nThe invoice will be permanently removed from the ledger and the cycle will move back to Pending so you can re-generate it.`;
        if (!window.confirm(msg)) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/sales/cycles/${c.id}/revert-invoice`);
            toast.success(data.deleted_invoice ? `Invoice ${c.invoice_number} deleted — cycle is back in Pending` : `Cycle restored to Pending (invoice was already gone)`);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Revert failed");
        } finally { setBusy(false); }
    };

    const entityName = (id) => entities?.find((e) => e.id === id)?.name || id || "—";

    return (
        <>
            <tr className={`border-b border-slate-100 ${selected ? "bg-blue-50/40" : ""}`} data-testid={`sales-cycle-row-${c.id}`}>
                {selectable && (
                    <td className="px-2 py-1.5 w-9">
                        <Checkbox
                            checked={!!selected}
                            onCheckedChange={onToggleSelect}
                            data-testid={`sales-cycle-select-${c.id}`}
                            aria-label={`Select cycle ${c.partner_name} ${c.cycle_start} to ${c.cycle_end}`}
                        />
                    </td>
                )}
                <td className="px-2 py-1.5 font-mono whitespace-nowrap">
                    <div className="text-[11.5px]">{c.cycle_start} → {c.cycle_end}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-widest">{c.cycle_type}</div>
                </td>
                <td className="px-2 py-1.5"><SegBadge segment={c.segment}/></td>
                <td className="px-2 py-1.5">{c.partner_name}</td>
                <td className="px-2 py-1.5 num">{fmtVol(c.our_volume, c.segment)}</td>
                <td className="px-2 py-1.5 num font-semibold">{num(c.our_amount)}</td>
                {kind === "invoiced" && <td className="px-2 py-1.5 font-mono">{c.invoice_number || "—"}</td>}
                {kind === "invoiced" && <td className="px-2 py-1.5 text-[11.5px] text-slate-600">{entityName(c.invoiced_entity_id)}</td>}
                {kind === "pending" && (
                    <td className="px-2 py-1.5">
                        {c.data_complete
                            ? <span className="text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">Complete</span>
                            : <span className="text-[10px] uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5" title={`Missing: ${(c.missing_dates || []).slice(0, 5).join(", ")}${(c.missing_dates || []).length > 5 ? "…" : ""}`}><AlertTriangle size={9} className="inline mr-0.5"/>{(c.missing_dates || []).length} day(s) missing</span>}
                    </td>
                )}
                <td className="px-2 py-1.5 whitespace-nowrap">
                    {kind === "pending" && canWrite && (
                        <>
                            <Button size="sm" className="h-7 px-2 rounded-sm bg-[#002FA7] hover:bg-[#001f6f]" onClick={() => setGenOpen(true)} disabled={busy} data-testid={`sales-cycle-generate-${c.id}`}>
                                <FilePlus2 size={12} className="mr-1"/>Generate
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-600" onClick={skip} disabled={busy} data-testid={`sales-cycle-skip-${c.id}`}>Skip</Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={remove} disabled={busy} data-testid={`sales-cycle-delete-${c.id}`} title="Delete this cycle (irreversible)"><Trash2 size={12}/></Button>
                        </>
                    )}
                    {kind === "invoiced" && (
                        <div className="flex items-center gap-1.5">
                            <CheckCircle2 size={14} className="text-emerald-600"/>
                            {canWrite && (
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={revertInvoice} disabled={busy} data-testid={`sales-cycle-revert-${c.id}`} title="Delete the invoice and move this cycle back to Pending">
                                    <Trash2 size={12} className="mr-1"/>Delete invoice
                                </Button>
                            )}
                        </div>
                    )}
                </td>
            </tr>
            {genOpen && <GenerateInvoiceDialog cycle={c} entities={entities} currencies={currencies} onClose={() => setGenOpen(false)} onChanged={onChanged}/>}
        </>
    );
}

function GenerateInvoiceDialog({ cycle, entities, currencies, onClose, onChanged }) {
    const today = new Date().toISOString().slice(0, 10);
    const defaultCurrency = cycle.segment === "Voice" ? "USD" : "EUR";
    const [form, setForm] = useState({
        entity_id: "",
        currency: defaultCurrency,
        invoice_number: "",
        invoice_date: today,
        description: "",
        amount_override: "",
    });
    const [hint, setHint] = useState("");
    const [busy, setBusy] = useState(false);

    // Auto-suggest invoice number when entity changes (uses sales-invoices/next-number).
    // We need partner_id — fetch partner by name first.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!form.entity_id) { setHint(""); return; }
            try {
                const { data: partners } = await api.get("/partners");
                const p = (partners || []).find((x) => (x.name || "").toLowerCase() === (cycle.partner_name || "").toLowerCase());
                if (!p) {
                    if (!cancelled) setHint(`No partner master found for "${cycle.partner_name}". Create the partner first or add a Server Name mapping — generation will fail.`);
                    return;
                }
                const { data } = await api.get("/sales-invoices/next-number", { params: { entity_id: form.entity_id, partner_id: p.id } });
                if (cancelled) return;
                if (data.next_number) {
                    setForm((f) => f.invoice_number ? f : { ...f, invoice_number: data.next_number });
                    setHint(`Suggested from previous ${data.based_on}. Editable.`);
                } else {
                    setHint("First invoice for this partner — will be auto-numbered.");
                }
            } catch {
                /* ignore */
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line
    }, [form.entity_id]);

    const submit = async () => {
        if (!form.entity_id) return toast.error("Entity is required");
        if (!form.currency) return toast.error("Currency is required");
        setBusy(true);
        try {
            const body = {
                entity_id: form.entity_id,
                currency: form.currency,
                invoice_date: form.invoice_date,
                description: form.description || null,
            };
            if (form.invoice_number.trim()) body.invoice_number = form.invoice_number.trim();
            if (form.amount_override !== "" && Number(form.amount_override) > 0) body.amount_override = Number(form.amount_override);
            const { data } = await api.post(`/sales/cycles/${cycle.id}/generate-invoice`, body);
            toast.success(`Invoice ${data.invoice_number} generated & posted`);
            onChanged?.();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Generate failed");
        } finally { setBusy(false); }
    };

    const currencyOptions = (currencies && currencies.length)
        ? currencies.map((c) => c.code || c)
        : ["USD", "EUR", "AED", "KWD", "INR", "USDT"];

    return (
        <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-sm max-w-2xl" data-testid="sales-recon-generate-dialog">
                <DialogHeader>
                    <DialogTitle>Generate Invoice — {cycle.partner_name}</DialogTitle>
                </DialogHeader>
                <div className="text-[12px] text-slate-500 -mt-2 mb-2 font-mono">
                    Cycle: {cycle.cycle_start} → {cycle.cycle_end} ({cycle.cycle_type}) · Volume: {fmtVol(cycle.our_volume, cycle.segment)} · Sales: {num(cycle.our_amount)}
                </div>
                {!cycle.data_complete && (
                    <div className="border border-amber-200 bg-amber-50 text-amber-800 text-[12px] px-3 py-2 mb-3 rounded-sm">
                        <AlertTriangle size={13} className="inline mr-1.5"/>
                        Daily data missing for {cycle.missing_dates?.length} day(s) — proceed only if you intend to invoice the partial period.
                    </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Entity (issuer)">
                        <Select value={form.entity_id} onValueChange={(v) => setForm({ ...form, entity_id: v, invoice_number: "" })}>
                            <SelectTrigger className="rounded-sm" data-testid="sales-recon-gen-entity"><SelectValue placeholder="Select entity"/></SelectTrigger>
                            <SelectContent>
                                {entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Currency">
                        <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                            <SelectTrigger className="rounded-sm" data-testid="sales-recon-gen-currency"><SelectValue/></SelectTrigger>
                            <SelectContent>
                                {currencyOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Invoice Number">
                        <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="rounded-sm h-9 font-mono" placeholder={form.entity_id ? "Leave blank for auto-suggest" : "Select entity first"} disabled={!form.entity_id} data-testid="sales-recon-gen-number"/>
                        {hint && <div className="text-[10.5px] text-slate-500 mt-1">{hint}</div>}
                    </Field>
                    <Field label="Invoice Date">
                        <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="sales-recon-gen-date"/>
                    </Field>
                    <div className="col-span-2">
                        <Field label={`Amount override (default: ${num(cycle.our_amount)})`}>
                            <Input type="number" step="0.01" value={form.amount_override} onChange={(e) => setForm({ ...form, amount_override: e.target.value })} className="rounded-sm h-9 font-mono" placeholder={`${num(cycle.our_amount)} (Cost IN total)`} data-testid="sales-recon-gen-amount"/>
                        </Field>
                    </div>
                    <div className="col-span-2">
                        <Field label="Line description (optional)">
                            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm h-9" placeholder={cycle.segment === "Voice" ? "A - Z Wholesale Voice Termination" : "A - Z Wholesale SMS Termination"} data-testid="sales-recon-gen-desc"/>
                        </Field>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || !form.entity_id} className="rounded-sm bg-[#002FA7]" data-testid="sales-recon-gen-submit">
                        <FilePlus2 size={14} className="mr-1.5"/>{busy ? "Generating…" : "Generate & Post"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function BulkGenerateInvoiceDialog({ cycles, entities, currencies, onClose, onChanged }) {
    const today = new Date().toISOString().slice(0, 10);
    const segments = useMemo(() => Array.from(new Set(cycles.map((c) => c.segment))), [cycles]);
    const totalSales = useMemo(() => cycles.reduce((s, c) => s + Number(c.our_amount || 0), 0), [cycles]);
    const incompleteCount = useMemo(() => cycles.filter((c) => !c.data_complete).length, [cycles]);

    const [entityId, setEntityId] = useState("");
    const [invoiceDate, setInvoiceDate] = useState(today);
    const [description, setDescription] = useState("");
    const [voiceCurrency, setVoiceCurrency] = useState("USD");
    const [smsCurrency, setSmsCurrency] = useState("EUR");
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [results, setResults] = useState(null);

    const currencyOptions = (currencies && currencies.length)
        ? currencies.map((c) => c.code || c)
        : ["USD", "EUR", "AED", "KWD", "INR", "USDT"];

    const submit = async () => {
        if (!entityId) return toast.error("Entity is required");
        setBusy(true);
        setResults(null);
        setProgress({ done: 0, total: cycles.length });
        const ok = [];
        const failed = [];
        // Sequential — keeps invoice-number auto-suggest deterministic per partner+entity.
        for (const c of cycles) {
            try {
                const cur = c.segment === "Voice" ? voiceCurrency : smsCurrency;
                const body = { entity_id: entityId, currency: cur, invoice_date: invoiceDate };
                if (description.trim()) body.description = description.trim();
                const { data } = await api.post(`/sales/cycles/${c.id}/generate-invoice`, body);
                ok.push({ cycle: c, invoice_number: data.invoice_number });
            } catch (e) {
                failed.push({ cycle: c, error: e.response?.data?.detail || e.message || "Generate failed" });
            } finally {
                setProgress((p) => ({ ...p, done: p.done + 1 }));
            }
        }
        setResults({ ok, failed });
        setBusy(false);
        if (ok.length) toast.success(`Generated ${ok.length} invoice(s)${failed.length ? ` — ${failed.length} failed` : ""}`);
        else if (failed.length) toast.error(`All ${failed.length} failed — see details below`);
    };

    const closeAndRefresh = () => { onChanged?.(); onClose(); };

    return (
        <Dialog open={true} onOpenChange={(v) => !v && (busy ? null : (results ? closeAndRefresh() : onClose()))}>
            <DialogContent className="rounded-sm max-w-2xl" data-testid="sales-recon-bulk-dialog">
                <DialogHeader>
                    <DialogTitle>Bulk Generate Invoices — {cycles.length} cycle{cycles.length === 1 ? "" : "s"}</DialogTitle>
                </DialogHeader>
                <div className="text-[12px] text-slate-500 -mt-1 mb-3 font-mono">
                    Σ Sales: {num(totalSales)} · Segments: {segments.join(" + ")}
                </div>
                {incompleteCount > 0 && !results && (
                    <div className="border border-amber-200 bg-amber-50 text-amber-800 text-[12px] px-3 py-2 mb-3 rounded-sm">
                        <AlertTriangle size={13} className="inline mr-1.5"/>
                        {incompleteCount} of {cycles.length} cycle{cycles.length === 1 ? " has" : "s have"} missing daily data. They will still be invoiced using only the days uploaded.
                    </div>
                )}

                {!results && (
                    <>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Entity (issuer) — applies to all">
                                <Select value={entityId} onValueChange={setEntityId}>
                                    <SelectTrigger className="rounded-sm" data-testid="sales-recon-bulk-entity"><SelectValue placeholder="Select entity"/></SelectTrigger>
                                    <SelectContent>
                                        {entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Invoice Date — applies to all">
                                <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className="rounded-sm h-9 font-mono" data-testid="sales-recon-bulk-date"/>
                            </Field>
                            {segments.includes("Voice") && (
                                <Field label="Voice currency">
                                    <Select value={voiceCurrency} onValueChange={setVoiceCurrency}>
                                        <SelectTrigger className="rounded-sm" data-testid="sales-recon-bulk-voice-currency"><SelectValue/></SelectTrigger>
                                        <SelectContent>
                                            {currencyOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </Field>
                            )}
                            {segments.includes("SMS") && (
                                <Field label="SMS currency">
                                    <Select value={smsCurrency} onValueChange={setSmsCurrency}>
                                        <SelectTrigger className="rounded-sm" data-testid="sales-recon-bulk-sms-currency"><SelectValue/></SelectTrigger>
                                        <SelectContent>
                                            {currencyOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </Field>
                            )}
                            <div className="col-span-2">
                                <Field label="Line description (optional — applies to all)">
                                    <Input value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-sm h-9" placeholder="Leave blank to use the per-segment default" data-testid="sales-recon-bulk-desc"/>
                                </Field>
                            </div>
                        </div>
                        <div className="mt-3 max-h-48 overflow-y-auto border border-slate-200 rounded-sm">
                            <table className="w-full text-[11.5px]">
                                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                                    <tr><th className="text-left px-2 py-1.5">Partner</th><th className="text-left px-2 py-1.5">Cycle</th><th className="text-left px-2 py-1.5">Seg</th><th className="text-right px-2 py-1.5">Sales</th></tr>
                                </thead>
                                <tbody>
                                    {cycles.map((c) => (
                                        <tr key={c.id} className="border-t border-slate-100">
                                            <td className="px-2 py-1">{c.partner_name}</td>
                                            <td className="px-2 py-1 font-mono">{c.cycle_start} → {c.cycle_end}</td>
                                            <td className="px-2 py-1">{c.segment}</td>
                                            <td className="px-2 py-1 text-right font-mono">{num(c.our_amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="mt-2 text-[11px] text-slate-500">
                            Invoice numbers are auto-suggested per partner (the next number after each partner's previous invoice on the chosen entity).
                        </div>
                    </>
                )}

                {busy && (
                    <div className="mt-3 text-[12px] text-slate-700">
                        Generating… <span className="font-mono">{progress.done} / {progress.total}</span>
                        <div className="mt-1 h-1.5 bg-slate-100 rounded-sm overflow-hidden">
                            <div className="h-full bg-[#002FA7] transition-all" style={{ width: `${progress.total ? (progress.done / progress.total * 100) : 0}%` }}/>
                        </div>
                    </div>
                )}

                {results && (
                    <div className="mt-2 space-y-2" data-testid="sales-recon-bulk-results">
                        <div className="flex items-center gap-3 text-[12px]">
                            <span className="text-emerald-700"><CheckCircle2 size={13} className="inline mr-1"/>{results.ok.length} succeeded</span>
                            {results.failed.length > 0 && <span className="text-red-700"><AlertTriangle size={13} className="inline mr-1"/>{results.failed.length} failed</span>}
                        </div>
                        {results.failed.length > 0 && (
                            <div className="max-h-44 overflow-y-auto border border-red-200 bg-red-50 rounded-sm">
                                <table className="w-full text-[11.5px]">
                                    <thead className="bg-red-100 text-[10px] uppercase tracking-widest text-red-800">
                                        <tr><th className="text-left px-2 py-1.5">Partner</th><th className="text-left px-2 py-1.5">Cycle</th><th className="text-left px-2 py-1.5">Error</th></tr>
                                    </thead>
                                    <tbody>
                                        {results.failed.map((f, i) => (
                                            <tr key={i} className="border-t border-red-200">
                                                <td className="px-2 py-1">{f.cycle.partner_name}</td>
                                                <td className="px-2 py-1 font-mono">{f.cycle.cycle_start} → {f.cycle.cycle_end}</td>
                                                <td className="px-2 py-1 text-red-700">{f.error}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    {!results ? (
                        <>
                            <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
                            <Button onClick={submit} disabled={busy || !entityId} className="rounded-sm bg-[#002FA7]" data-testid="sales-recon-bulk-submit">
                                <FilePlus2 size={14} className="mr-1.5"/>{busy ? `Generating ${progress.done}/${progress.total}…` : `Generate ${cycles.length}`}
                            </Button>
                        </>
                    ) : (
                        <Button onClick={closeAndRefresh} className="rounded-sm bg-[#002FA7]" data-testid="sales-recon-bulk-done">Done</Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
